let verbose = false;
let saveLogs = true;
let environment = "dev"; // Default environment is 'prod'
let timeLogs = {}; // Object to store time logs

const queue = []; // Queue to store log messages awaiting write

let isWriting = false;

// Format date time in yyyy-mm-dd HH:mm:ss AM/PM format
const formatDateTime = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  hours = String(hours).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} ${ampm}`;
};

const getCurrentTime = () => {
  const now = new Date();
  let hours = now.getHours();
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  hours = String(hours).padStart(2, "0");
  return `${hours}:${minutes}:${seconds} ${ampm} /`;
};

// In-memory debug logs with deduplication
const debugLogs = {
  logs: [],
  logHashes: new Map(), // Track hashes for deduplication
  dedupeWindow: 5000, // 5 seconds window for deduplication
  maxLogs: 10000, // Hard cap: keep only the most recent 10k logs in memory
  maxLogAge: 30 * 60 * 1000, // Maximum age of logs in memory (30 minutes)
  cleanupInterval: null, // Interval ID for periodic cleanup
  lastCleanupTime: 0, // Last cleanup timestamp
  cleanupIntervalMs: 60000, // Run cleanup every 60 seconds
  batchCleanupSize: 1000, // Number of old logs to remove per cleanup cycle
  stats: {
    totalAdded: 0,
    totalDeduped: 0,
    totalExported: 0,
    totalCleaned: 0,
  },

  // Generate hash for deduplication
  generateHash: function (level, category, message, context) {
    // Create a simplified context for hashing (only meaningful fields)
    const filteredContext = filterMeaningfulContext(category, context);
    const contextStr = JSON.stringify(
      filteredContext,
      Object.keys(filteredContext).sort()
    );
    return `${level}:${category}:${message}:${contextStr}`;
  },

  // Check if log should be deduplicated
  shouldDedupe: function (hash, timestamp, skipDedupe) {
    if (skipDedupe) return false;
    const lastTime = this.logHashes.get(hash);
    if (lastTime && timestamp - lastTime < this.dedupeWindow) {
      return true;
    }
    this.logHashes.set(hash, timestamp);

    // Periodic cleanup of old hash entries (every 1000 additions)
    if (this.stats.totalAdded % 1000 === 0) {
      this.cleanupOldHashes();
    }

    return false;
  },

  // Clean up old hash entries that are beyond dedupe window
  cleanupOldHashes: function () {
    const now = Date.now();
    const cutoffTime = now - this.dedupeWindow;
    let cleaned = 0;

    for (const [hash, timestamp] of this.logHashes.entries()) {
      if (timestamp < cutoffTime) {
        this.logHashes.delete(hash);
        cleaned++;
      }
    }

    // Limit hash map size to prevent unbounded growth
    if (this.logHashes.size > this.maxLogs * 2) {
      // Keep only most recent hashes
      const entries = Array.from(this.logHashes.entries())
        .sort((a, b) => b[1] - a[1]) // Sort by timestamp descending
        .slice(0, this.maxLogs);
      this.logHashes.clear();
      entries.forEach(([hash, timestamp]) => {
        this.logHashes.set(hash, timestamp);
      });
    }
  },

  // Clean up old logs based on age and count
  cleanupOldLogs: function () {
    const now = Date.now();
    const ageCutoff = now - this.maxLogAge;
    let cleaned = 0;

    // Remove logs older than maxLogAge
    while (this.logs.length > 0 && this.logs[0].timestamp < ageCutoff) {
      this.logs.shift();
      cleaned++;
    }

    // If still over maxLogs, remove oldest logs in batches
    if (this.logs.length > this.maxLogs) {
      const toRemove = Math.min(
        this.batchCleanupSize,
        this.logs.length - this.maxLogs
      );
      for (let i = 0; i < toRemove; i++) {
        this.logs.shift();
        cleaned++;
      }
    }

    // Clean up hashes for removed logs
    if (cleaned > 0) {
      this.cleanupOldHashes();
      this.stats.totalCleaned += cleaned;
    }

    this.lastCleanupTime = now;
    return cleaned;
  },

  // Start periodic cleanup
  startPeriodicCleanup: function () {
    if (this.cleanupInterval) return; // Already started

    this.cleanupInterval = setInterval(() => {
      this.cleanupOldLogs();
    }, this.cleanupIntervalMs);

    // Also run cleanup on first start
    this.cleanupOldLogs();
  },

  // Stop periodic cleanup
  stopPeriodicCleanup: function () {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  },

  // Add log entry
  add: function (level, category, message, context = {}, skipDedupe = false) {
    const timestamp = Date.now();
    const hash = this.generateHash(level, category, message, context);

    // Check deduplication
    if (this.shouldDedupe(hash, timestamp, skipDedupe)) {
      this.stats.totalDeduped++;
      return;
    }

    // Filter context to remove redundant information
    const filteredContext = filterMeaningfulContext(category, context);

    // Add log entry
    const logEntry = {
      timestamp,
      level,
      category,
      message,
      context: filteredContext,
    };

    this.logs.push(logEntry);
    this.stats.totalAdded++;

    // Start periodic cleanup on first log
    if (!this.cleanupInterval && this.stats.totalAdded === 1) {
      this.startPeriodicCleanup();
    }

    // Aggressive cleanup: if approaching maxLogs, clean up proactively
    if (this.logs.length > this.maxLogs * 0.9) {
      // Remove oldest 10% of logs when we hit 90% capacity
      const toRemove = Math.floor(this.logs.length * 0.1);
      for (let i = 0; i < toRemove; i++) {
        this.logs.shift();
        this.stats.totalCleaned++;
      }
      this.cleanupOldHashes();
    }
  },

  // Export logs to text format
  export: function (options = {}) {
    const {
      includeDedupeInfo = false,
      filterCategory = null,
      maxLines = 10000,
    } = options;

    // Filter logs by category if specified
    let filteredLogs = this.logs;
    if (filterCategory) {
      filteredLogs = this.logs.filter((log) => log.category === filterCategory);
    }

    // Limit number of logs
    const logsToExport = filteredLogs.slice(-maxLines);

    // Format logs
    const formattedLogs = logsToExport.map((log) => {
      const date = new Date(log.timestamp);
      const dateTime = formatDateTime(date);

      // Format context
      const contextStr =
        Object.keys(log.context).length > 0
          ? ` | Context: ${JSON.stringify(log.context)}`
          : "";

      return `[${dateTime}] [${log.level.toUpperCase()}] [${log.category}] ${
        log.message
      }${contextStr}`;
    });

    // Add deduplication stats if requested
    if (includeDedupeInfo) {
      formattedLogs.unshift("");
      formattedLogs.unshift(`=== Debug Log Statistics ===`);
      formattedLogs.unshift(`Total logs added: ${this.stats.totalAdded}`);
      formattedLogs.unshift(
        `Total logs deduplicated: ${this.stats.totalDeduped}`
      );
      formattedLogs.unshift(`Total logs cleaned: ${this.stats.totalCleaned}`);
      formattedLogs.unshift(`Total logs exported: ${logsToExport.length}`);
      formattedLogs.unshift(`Current logs in memory: ${this.logs.length}`);
      formattedLogs.unshift(`Current hash entries: ${this.logHashes.size}`);
      formattedLogs.unshift(`Deduplication window: ${this.dedupeWindow}ms`);
      formattedLogs.unshift(
        `Max log age: ${this.maxLogAge / 1000 / 60} minutes`
      );
      formattedLogs.unshift(`Max logs limit: ${this.maxLogs}`);
    }

    this.stats.totalExported++;
    return formattedLogs.join("\n");
  },

  // Clear all logs
  clear: function () {
    this.stopPeriodicCleanup();
    this.logs = [];
    this.logHashes.clear();
    this.stats = {
      totalAdded: 0,
      totalDeduped: 0,
      totalExported: 0,
      totalCleaned: 0,
    };
    this.lastCleanupTime = 0;
  },

  // Get statistics
  getStats: function () {
    return {
      ...this.stats,
      currentLogs: this.logs.length,
      currentHashes: this.logHashes.size,
      dedupeWindow: this.dedupeWindow,
      maxLogs: this.maxLogs,
      maxLogAge: this.maxLogAge,
      lastCleanupTime: this.lastCleanupTime,
    };
  },
};

// Filter meaningful context based on category
const filterMeaningfulContext = (category, context) => {
  if (!context || typeof context !== "object") return {};

  const filtered = {};

  // Category-specific filtering
  switch (category) {
    case "file":
      // Keep essential file operation info
      if (context.fileId !== undefined) filtered.fileId = context.fileId;
      if (context.fileName !== undefined) filtered.fileName = context.fileName;
      if (context.fileSize !== undefined) filtered.fileSize = context.fileSize;
      if (context.duration !== undefined) filtered.duration = context.duration;
      if (context.response !== undefined) filtered.response = context.response;
      if (context.retryCount !== undefined && context.retryCount > 0)
        filtered.retryCount = context.retryCount;
      if (context.totalChunks !== undefined)
        filtered.totalChunks = context.totalChunks;
      if (context.resumeLoc !== undefined)
        filtered.resumeLoc = context.resumeLoc;
      if (context.error !== undefined) filtered.error = context.error;
      if (context.callAttempt !== undefined)
        filtered.callAttempt = context.callAttempt;
      if (context.timestamp !== undefined)
        filtered.timestamp = context.timestamp;
      if (context.alreadyClosed !== undefined)
        filtered.alreadyClosed = context.alreadyClosed;
      // Remove all other fields - we only want minimal info
      break;

    case "chunk":
      // Keep essential chunk info
      if (context.chunk !== undefined) filtered.chunk = context.chunk;
      if (context.socket !== undefined) filtered.socket = context.socket;
      if (context.retries !== undefined && context.retries > 0)
        filtered.retries = context.retries;
      if (context.error !== undefined) filtered.error = context.error;
      if (context.retry !== undefined && context.retry > 0)
        filtered.retry = context.retry;
      break;

    case "socket":
      // Keep essential socket info
      if (context.socketIndex !== undefined)
        filtered.socketIndex = context.socketIndex;
      if (context.timeMs !== undefined) filtered.timeMs = context.timeMs;
      if (context.error !== undefined) filtered.error = context.error;
      if (context.isRetrySocket !== undefined)
        filtered.isRetrySocket = context.isRetrySocket;
      break;

    case "upload":
      // Keep essential upload info
      if (context.duration !== undefined) filtered.duration = context.duration;
      if (context.chunks !== undefined) filtered.chunks = context.chunks;
      break;

    default:
      // For other categories, keep all context but filter out undefined/null
      for (const key in context) {
        // Skip fileId/fileName if they're always the same
        if (key === "fileName" && category !== "upload") continue;
        if (context[key] !== undefined && context[key] !== null) {
          filtered[key] = context[key];
        }
      }
  }

  return filtered;
};

const C = {
  setVerbose: (value) => {
    verbose = value;
  },
  setSaveLogs: (value) => {
    saveLogs = value;
  },
  setEnvironment: (env) => {
    environment = env;
  },
  // Enhanced logging methods
  debugLogs: debugLogs,

  /**
   * Log a verbose action with context (stored in memory only)
   * @param {string} category - Action category (e.g., 'upload', 'chunk', 'socket')
   * @param {string} message - Action description
   * @param {Object} context - Additional context/metadata
   * @param {boolean} skipDedupe - Skip deduplication check (default: false)
   */
  logAction: (category, message, context = {}, skipDedupe = false) => {
    debugLogs.add("action", category, message, context, skipDedupe);

    // Also log to console if verbose
    if (verbose || environment === "dev" || environment === "staging") {
      console.log(`[ACTION:${category}]`, message, context);
    }
  },

  /**
   * Log debug information (stored in memory only)
   * @param {string} category - Debug category
   * @param {string} message - Debug message
   * @param {Object} context - Additional context/metadata
   */
  logDebug: (category, message, context = {}) => {
    debugLogs.add("debug", category, message, context);
  },

  /**
   * Export debug logs to text format
   * @param {Object} options - Export options
   * @returns {string} Formatted debug logs
   */
  exportDebugLogs: (options = {}) => {
    return debugLogs.export(options);
  },

  /**
   * Get debug log statistics
   * @returns {Object} Statistics object
   */
  getDebugLogStats: () => {
    return debugLogs.getStats();
  },

  /**
   * Clear all debug logs
   */
  clearDebugLogs: () => {
    debugLogs.clear();
  },

  log: (...args) => {
    const message = args.join(" ");
    if (environment === "dev" || environment === "staging") {
      console.log(getCurrentTime(), ...args);
    } else if (verbose) {
      console.trace(getCurrentTime(), ...args);
    }
    if (saveLogs) {
      C.saveToPersistentStorage(message);
    }
    // Also add to debug logs
    debugLogs.add("info", "general", message, {});
  },
  table: (...args) => {
    console.table(...args);
  },
  trace: (...args) => {
    const message = args.join(" ");
    if (environment === "dev" || environment === "staging") {
      console.trace(getCurrentTime(), ...args);
    }
    if (saveLogs) {
      C.saveToPersistentStorage(message);
    }
    // Also add to debug logs
    debugLogs.add("trace", "general", message, {});
  },
  warn: (message) => {
    console.warn(
      `%c ${getCurrentTime()} ${message}`,
      "color: white;background-color:orange;font-weight: bold"
    );
    if (saveLogs) {
      C.saveToPersistentStorage("Warning:" + message);
    }
    // Also add to debug logs
    debugLogs.add("warn", "warning", message, {});
  },
  error: (message) => {
    console.warn(
      `%c ${getCurrentTime()} ${message}`,
      "color: white;background-color:red;font-weight: bold"
    );
    if (saveLogs) {
      C.saveToPersistentStorage("Error:" + message);
    }
    // Also add to debug logs
    debugLogs.add("error", "error", message, {});
  },
  store: (message) => {
    if (saveLogs) {
      C.saveToPersistentStorage("Detailed:" + message);
    }
    // Also add to debug logs
    debugLogs.add("info", "store", message, {});
  },
  success: (message) => {
    console.warn(
      `%c ${getCurrentTime()} ${message}`,
      "color: white;background-color:green;font-weight: bold"
    );
    if (saveLogs) {
      C.saveToPersistentStorage("Success:" + message);
    }
    // Also add to debug logs
    debugLogs.add("success", "success", message, {});
  },
  time: (label) => {
    timeLogs[label] = new Date();
    console.time(label);
  },
  timeEnd: (label) => {
    console.timeEnd(label);
    if (saveLogs) {
      const startTime = timeLogs[label];
      if (startTime) {
        const endTime = new Date();
        const elapsedTime = endTime - startTime;
        const logMessage = `${label}: ${elapsedTime}ms`;
        C.saveToPersistentStorage(logMessage);
      } else {
        console.error(`No such label '${label}' for time measurement.`);
      }
    }
  },
  saveToPersistentStorage: (message) => {
    queue.push(message); // Enqueue the message
    C.writeFromQueue(); // Start writing from the queue
  },
  writeFromQueue: () => {
    if (!isWriting && queue.length > 0) {
      isWriting = true;
      const message = queue.shift(); // Dequeue the first message
      C.writeMessage(message)
        .then(() => {
          isWriting = false;
          C.writeFromQueue(); // Continue writing from the queue
        })
        .catch((error) => {
          // console.error("Error writing message:", error);
          isWriting = false;
          C.writeFromQueue(); // Continue writing from the queue
        });
    }
  },
  // Function to write a single log message
  writeMessage: (message) => {
    return new Promise((resolve, reject) => {
      try {
        window.requestFileSystem =
          window.requestFileSystem || window.webkitRequestFileSystem;

        window.requestFileSystem(
          window.PERSISTENT,
          0,
          (fs) => {
            checkStorageDuration();
            fs.root.getFile(
              "logs.txt",
              { create: true },
              (fileEntry) => {
                fileEntry.createWriter((fileWriter) => {
                  fileWriter.onwriteend = () => {
                    resolve(); // Resolve the promise once write is completed
                  };

                  fileWriter.onerror = (error) => {
                    reject(error); // Reject the promise if there's an error
                  };

                  fileWriter.seek(fileWriter.length);
                  const formattedMessage = formatMessage(message);
                  const blob = new Blob([formattedMessage + "\n"], {
                    type: "text/plain",
                  });
                  fileWriter.write(blob);
                });
              },
              (err) => {
                reject(err); // Reject the promise if file access fails
              }
            );
          },
          (err) => {
            reject(err); // Reject the promise if file system access fails
          }
        );
      } catch (e) {
        reject(e); // Reject the promise if an exception occurs
        // console.error(e);
      }
    });
  },
  downloadLogs: (onlyClear) => {
    try {
      if (saveLogs) {
        window.requestFileSystem =
          window.requestFileSystem || window.webkitRequestFileSystem;

        window.requestFileSystem(
          window.PERSISTENT,
          0,
          (fs) => {
            fs.root.getFile("logs.txt", {}, (fileEntry) => {
              fileEntry.file((file) => {
                if (!onlyClear) {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    // Create a link element to download the logs
                    const downloadLink = document.createElement("a");
                    downloadLink.href = reader.result;
                    downloadLink.download = "logs.txt";
                    downloadLink.click();
                    setTimeout(() => {
                      fileEntry.remove(() => {
                        console.log("Logs cleared.");
                      });
                    }, 5000);
                  };
                  reader.onerror = (err) => {
                    console.error("Error reading file:", err);
                  };
                  reader.readAsDataURL(file);
                } else {
                  fileEntry.remove(() => {
                    console.log("Logs cleared.");
                  });
                }
              });
            });
          },
          (err) => {
            console.error("Failed to request file system:", err);
          }
        );
      } else {
        console.log("Logs are not being saved.");
      }
    } catch (e) {
      console.log(e);
    }
  },
};

const checkStorageDuration = () => {
  // Function kept for compatibility but no longer needed
  // Delta time calculation has been removed
};
const formatMessage = (...args) => {
  const message = args.join(" ");
  return `[${getCurrentDateTime()}] ${message}`;
};

const getCurrentDateTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  const milliseconds = String(now.getMilliseconds()).padStart(3, "0");

  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
};

const addTimestamp = (message) => {
  return `${getCurrentDateTime()} | ${message}`;
};
export default C;
